import React, { useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import * as LucideIcons from 'lucide-react';

import { useAppContext } from '../AppContext';
import InviteModal from './InviteModal';
import { useTranslation } from 'react-i18next';

function NexusHeader({ nexus, nexusId }) {
    const { t } = useTranslation();
    const { userId, appFunctions, showConfirmation, handlePageChange, setMessage } = useAppContext();
    const isMember = nexus.memberIds?.includes(userId) ?? false;
    const isOwner = nexus.ownerId === userId;
    const [isProcessing, setIsProcessing] = useState(false);
    const [showInviteModal, setShowInviteModal] = useState(false);

    const handleJoin = async () => {
        setIsProcessing(true);
        const joinNexusFn = httpsCallable(appFunctions, 'joinNexus');
        try {
            await joinNexusFn({ nexusId: nexusId });
            setMessage(t('nexusHeader.joinSuccess', { nexusName: nexus.name }));
        } catch (error) { setMessage(`${t('nexusHeader.joinFailed')} ${error.message}`); }
        finally { setIsProcessing(false); }
    };

    const handleLeave = () => {
        showConfirmation({
            message: `Are you sure you want to leave ${nexus.name}? You will lose your current Faction status, but you will be free to pledge to a new Nexus.`,
            onConfirm: async () => {
                setIsProcessing(true);
                const leaveNexusFn = httpsCallable(appFunctions, 'leaveNexus');
                try {
                    await leaveNexusFn({ nexusId: nexusId });
                    setMessage(`You have left ${nexus.name}. You are now free to join a new Faction.`);
                    handlePageChange('nexus'); // Go back to the portal
                } catch (error) { setMessage(`${t('nexusHeader.leaveFailed')} ${error.message}`); }
                finally { setIsProcessing(false); }
            }
        });
    };

    const handleDelete = () => {
        showConfirmation({
            message: t('nexusHeader.deleteConfirmation', { nexusName: nexus.name }),
            onConfirm: async () => {
                setIsProcessing(true);
                const deleteNexusFn = httpsCallable(appFunctions, 'deleteNexus');
                try {
                    await deleteNexusFn({ nexusId: nexusId });
                    setMessage(t('nexusHeader.deleteSuccess', { nexusName: nexus.name }));
                    handlePageChange('nexus');
                } catch (error) { setMessage(`${t('nexusHeader.deleteFailed')} ${error.message}`); }
                finally { setIsProcessing(false); }
            }
        });
    };

    const luminancePercentage = (nexus.luminance / nexus.luminanceToNextLevel) * 100;
    const aura = nexus.currentAura || { mood: 'New', color: nexus.nexusColor, summary: 'A new Nexus, ready to be shaped.' };

    const factionEmblem = nexus.emblem || 'Star';
    const factionMotto = nexus.motto || 'We reach for the stars';
    const factionType = nexus.factionType || 'Stargazers';
    const FactionIcon = LucideIcons[factionEmblem] || LucideIcons.Star;

    return (
        <>
            <div className="relative p-6 bg-gradient-to-br from-gray-900/90 to-black/95 rounded-2xl shadow-[0_0_40px_rgba(139,92,246,0.15)] border border-white/10 mb-8 overflow-hidden group">
                {/* Dynamic Particle Effect / Background */}
                <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-white/5 to-transparent opacity-50"></div>
                <div className="absolute inset-0 nexus-aura-bg opacity-30 group-hover:opacity-50 transition-opacity duration-1000" style={{ '--nexus-color': aura.color }}></div>
                
                {/* Header Content */}
                <div className="relative z-10 flex flex-col md:flex-row items-center md:items-end gap-6 h-auto md:h-48">
                    
                    {/* Emblem Shield */}
                    <div className="relative flex-shrink-0">
                        <div className="absolute inset-0 rounded-full animate-pulse-slow blur-xl opacity-60" style={{ backgroundColor: aura.color }}></div>
                        <div className="relative w-28 h-28 sm:w-36 sm:h-36 rounded-full border-2 border-white/20 bg-black/60 backdrop-blur-sm shadow-2xl flex items-center justify-center p-6" style={{ borderColor: aura.color }}>
                            <FactionIcon className="w-full h-full drop-shadow-md" style={{ color: aura.color }} strokeWidth={1.5} />
                        </div>
                    </div>

                    <div className="flex-1 text-center md:text-left min-w-0 pb-2">
                        {/* Faction Type & Level */}
                        <div className="flex items-center justify-center md:justify-start gap-3 mb-2">
                            <span className="px-3 py-1 rounded-full text-xs font-bold tracking-widest uppercase bg-white/10 border border-white/20 text-white shadow-sm backdrop-blur-md">
                                {factionType}
                            </span>
                            <span className="text-sm font-bold" style={{ color: aura.color }}>Level {nexus.level}</span>
                        </div>
                        
                        {/* Nexus Name */}
                        <h2 className="text-4xl md:text-5xl lg:text-6xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-white to-gray-400 font-playfair truncate drop-shadow-md mb-2">
                            {nexus.name}
                        </h2>
                        
                        {/* Motto */}
                        <p className="text-gray-300 italic text-sm md:text-base tracking-wide border-l-2 pl-3" style={{ borderColor: aura.color }}>
                            "{factionMotto}"
                        </p>
                    </div>

                    <div className="flex-shrink-0 flex items-center gap-3 pb-2">
                        {isOwner && (
                            <button onClick={handleDelete} disabled={isProcessing} className="p-3 bg-red-900/50 hover:bg-red-800/80 border border-red-500/30 text-white rounded-xl shadow-lg transition-all hover:scale-105">
                                <LucideIcons.Trash2 size={18} />
                            </button>
                        )}
                        {isMember ? (
                            <>
                                <button onClick={() => setShowInviteModal(true)} disabled={isProcessing} className="px-4 py-2 bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-500 hover:to-blue-500 text-white font-bold rounded-xl shadow-lg shadow-cyan-500/20 transition-all hover:scale-105 border border-white/10 flex items-center gap-2">
                                    <LucideIcons.UserPlus size={18} /> Invite
                                </button>
                                <button onClick={handleLeave} disabled={isProcessing} className="px-4 py-2 bg-gray-800/80 hover:bg-gray-700/80 border border-gray-600 text-white font-bold rounded-xl shadow-lg transition-all hover:scale-105">
                                    {t('nexusHeader.leaveButton')}
                                </button>
                            </>
                        ) : (
                            <button onClick={handleJoin} disabled={isProcessing} className="px-6 py-2 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500 text-white font-bold rounded-xl shadow-lg shadow-purple-500/20 transition-all hover:scale-105 border border-white/10 flex items-center gap-2">
                                <LucideIcons.LogIn size={18} /> {t('nexusHeader.joinButton')}
                            </button>
                        )}
                    </div>
                </div>

                {/* Aura Summary & Luminance Bar */}
                <div className="mt-8 relative z-10 px-2">
                    <div className="flex justify-between items-center text-xs font-bold mb-2 uppercase tracking-wider text-gray-400">
                        <span>Aura: <span style={{ color: aura.color }}>{aura.mood}</span></span>
                        <span>{nexus.luminance} / {nexus.luminanceToNextLevel} Luminance</span>
                    </div>
                    <div className="w-full bg-black/60 rounded-full h-3 border border-white/5 overflow-hidden shadow-inner">
                        <div className="h-full rounded-full transition-all duration-1000 ease-out relative overflow-hidden" style={{ width: `${luminancePercentage}%`, backgroundColor: aura.color }}>
                            <div className="absolute inset-0 bg-white/20 w-full animate-pulse-slow"></div>
                        </div></div>
                </div>
            </div>
            {showInviteModal && <InviteModal nexusId={nexusId} onClose={() => setShowInviteModal(false)} />}
        </>
    );
}


export default NexusHeader;